export interface OrderRequestLease {
  orderId: string
  generation: number
  token: symbol
}

export interface OrderRequestGuard {
  isCurrent(
    lease: OrderRequestLease,
    currentOrderId: string | undefined,
  ): boolean
}

export const runOrderScopedRequest = async <T>(args: {
  guard: OrderRequestGuard
  lease: OrderRequestLease
  getCurrentOrderId: () => string | undefined
  request: () => Promise<T>
  onSuccess: (result: T) => void
  onError: (error: unknown) => void
}) => {
  const { guard, lease, getCurrentOrderId, request, onSuccess, onError } = args
  try {
    const result = await request()
    if (!guard.isCurrent(lease, getCurrentOrderId())) return false
    onSuccess(result)
    return true
  } catch (error) {
    if (!guard.isCurrent(lease, getCurrentOrderId())) return false
    onError(error)
    return false
  }
}

export const canStartAutoPrint = (status: string) =>
  status !== 'pending' && status !== 'success'

export const createOrderRequestGuard = () => {
  let selectedOrderId: string | undefined
  let generation = 0
  let activeToken: symbol | null = null

  const selectOrder = (orderId: string | undefined) => {
    if (orderId !== selectedOrderId) {
      selectedOrderId = orderId
      generation += 1
      activeToken = null
    }
  }

  return {
    selectOrder,
    begin(orderId: string): OrderRequestLease {
      selectOrder(orderId)
      const token = Symbol('order-request')
      activeToken = token
      return { orderId, generation, token }
    },
    isCurrent(lease: OrderRequestLease, currentOrderId: string | undefined) {
      return (
        lease.orderId === currentOrderId &&
        lease.orderId === selectedOrderId &&
        lease.generation === generation &&
        lease.token === activeToken
      )
    },
    invalidate() {
      selectedOrderId = undefined
      generation += 1
      activeToken = null
    },
  }
}

export interface KitNewSkuModalReset {
  contextKey: string
  isOpen: boolean
  barcode: string
  isSubmitting: boolean
}

export const getKitNewSkuContextKey = (args: {
  orderId: string | undefined
  kitVariationId: string | undefined
  unitIndex: number | undefined
  stickerPrinted: boolean
}) =>
  JSON.stringify([
    args.orderId,
    args.kitVariationId,
    args.unitIndex,
    args.stickerPrinted,
  ])

export const getKitNewSkuModalReset = (
  stickerPrinted: boolean,
  contextKey: string,
): KitNewSkuModalReset => ({
  contextKey,
  isOpen: stickerPrinted,
  barcode: '',
  isSubmitting: false,
})

export const syncKitNewSkuModalState = (
  state: KitNewSkuModalReset,
  contextKey: string,
  stickerPrinted: boolean,
): KitNewSkuModalReset =>
  state.contextKey === contextKey
    ? state
    : getKitNewSkuModalReset(stickerPrinted, contextKey)
